import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { ProjectApi } from '../../../network';
import { ProjectAdminModel } from '../../../network/rest';
import ApiErrorHandler from '../../../utils/ApiErrorHandler';
import { translations } from '../../../utils/staticContent';
import ImageUploadForm from '../../components/ImageUploadForm';
import RichTextEditor from '../../components/RichTextEditor/RichTextEditor';

export default function ProjectForm({ project }: { project?: ProjectAdminModel }) {
    useEffect(() => {
        if (project) {
            getData(project.id);
        }
    }, []);

    async function getData(id: string) {
        try {
            const response = await ProjectApi.getProjectAdminApiV1ProjectsProjectAdminGet({
                id,
            });
            if (response) {
                setTitle(response.title);
                setContent(response.content);
                setType(response.type);
                setAuthor(response.author);
            }
        } catch (e) {
            ApiErrorHandler(e);
        }
    }

    const [title, setTitle] = useState(project?.title || '');
    const [content, setContent] = useState(project?.content || '');
    const [type, setType] = useState(project?.type || '');
    const [author, setAuthor] = useState(project?.author || '');
    const [fileId, setFileId] = useState('');

    const [error, setError] = useState('');

    return (
        <div className="ProjectForm">
            <Container component="main" maxWidth="md">
                <Grid
                    container
                    component="form"
                    rowSpacing={2}
                    columnSpacing={{ xs: 1, sm: 2 }}
                    noValidate
                    mt={1}
                    onSubmit={async (e) => {
                        e.preventDefault();
                        try {
                            if (project) {
                                await ProjectApi.updateProjectApiV1ProjectsProjectPut({
                                    id: project.id,
                                    projectUpdateModel: {
                                        title,
                                        content,
                                        type,
                                    },
                                });
                            } else {
                                await ProjectApi.createProjectApiV1ProjectsProjectPost({
                                    projectCreateModel: {
                                        title,
                                        content,
                                        type,
                                        author,
                                        fileId,
                                    },
                                });
                                setTitle('');
                                setContent('');
                                setType('');
                                setAuthor('');
                            }
                        } catch (e) {
                            const error = ApiErrorHandler(e);
                            setError(error);
                        }
                    }}
                >
                    <Grid item xs={12} sm={12}>
                        <TextField
                            required
                            fullWidth
                            id="title"
                            label={translations.title}
                            name="title"
                            autoFocus
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            required
                            fullWidth
                            id="type"
                            value={type}
                            name="type"
                            label={translations.type}
                            autoFocus
                            onChange={(e) => setType(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            required
                            fullWidth
                            name="author"
                            label={translations.author}
                            id="author"
                            value={author}
                            onChange={(e) => setAuthor(e.target.value)}
                            disabled={!!project}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <RichTextEditor
                            value={content}
                            onChange={setContent}
                            label={translations.content}
                        />
                    </Grid>
                    <ImageUploadForm setError={setError} fileId={fileId} setFileId={setFileId} />
                    <Grid item xs={12} sm={12}>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2 }}
                            disabled={
                                title.length < 5 ||
                                title.length > 50 ||
                                !type.length ||
                                content.length < 5 ||
                                content.length > 5000 ||
                                author.length < 2 ||
                                author.length > 20
                            }
                        >
                            {translations.save}
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <Typography component="span" color="red">
                            {error}
                        </Typography>
                    </Grid>
                </Grid>
            </Container>
        </div>
    );
}
